import { useContext, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { EntityMilestoneAndDurationColumns } from "entity-app/shared-components/entity-milestones-duration-columns";
import { usePreconstrutionAgGrid } from "pages/preconstruction-log-page/hooks/preconstruction-ag-grid-data";
import { BID_PACKAGE_LIST_VIEW } from "pages/preconstruction-log-page/queries";
import { joinEarlyTenderReason } from "pages/preconstruction-log-page/hooks/early-tender-reason";
import AgGridPrintLayout from "./ag-grid-print-layout";
import { BidPackageLogPrintViewState } from "./model";

function BidPackageLogPrintView({
  state
}: {
  state: BidPackageLogPrintViewState;
}) {
  const {
    columnHeaders: { bidPackageHeaderMap },
    gqlClientForProject
  } = useContext(ProjectContext);

  const gridRef = useRef<AgGridReact<any>>(null);

  const { data, loading } = useCIQQuery<{
    bid_package_list_func: Array<any>;
    feature_workflow_instance: Array<{
      id: string;
      wf_override_reason: string | null;
    }>;
  }>(BID_PACKAGE_LIST_VIEW, {
    variables: {},
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const rowData = useMemo(
    () => joinEarlyTenderReason(data)?.bid_package_list_func,
    [data]
  );

  // Mirror preconstruction-data.ts exactly so the milestone colIds align with
  // the saved grid state passed in `state.initialState`.
  const maxPossibleMilestones = useMemo(() => {
    const list = rowData || [];
    return list.reduce((max: number, item: any) => {
      const milestones = item?.milestones;
      return Array.isArray(milestones) ? Math.max(max, milestones.length) : max;
    }, 0);
  }, [rowData]);

  const milestonesColumns = useMemo(
    () =>
      EntityMilestoneAndDurationColumns(
        maxPossibleMilestones > 0
          ? [{ milestones: new Array(maxPossibleMilestones).fill({}) }]
          : []
      ),
    [maxPossibleMilestones]
  );

  const { columnDefs } = usePreconstrutionAgGrid({
    featureKey: "bid-packages",
    milestonesColumns,
    cannotWriteAccessOfBidPackage: true,
    bidPackageHeader: bidPackageHeaderMap
  });

  return (
    <AgGridPrintLayout
      gridRef={gridRef}
      state={state}
      columnDefEntity={columnDefs}
      rowData={rowData}
      rowLoading={loading}
      wrapHeaderText
    />
  );
}

export default BidPackageLogPrintView;
